import { useEffect, useState, useRef, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

export const useWebSocket = (organizationId, userId, onMessageReceived) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const stompClientRef = useRef(null);

  const onMessageReceivedRef = useRef(onMessageReceived);
  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived;
  }, [onMessageReceived]);

  const getWebSocketUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3030/ws';
    }
    return 'https://whatsapp.jipate.com/ws';
  };

  const connect = useCallback(() => {
    if (!organizationId) {
      console.warn('No organizationId provided for WebSocket');
      return null;
    }

    const wsUrl = getWebSocketUrl();
    console.log('🔌 Connecting to WebSocket:', wsUrl);

    const socket = new SockJS(wsUrl);

    const stompClient = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      debug: (str) => {
      },
      onConnect: () => {
        console.log('✅ WebSocket connected to:', wsUrl);
        setIsConnected(true);
        setConnectionError(null);

        // Inbox messages, mode changes, escalations — org-wide topic
        stompClient.subscribe(`/topic/inbox/${organizationId}`, (message) => {
          try {
            const data = JSON.parse(message.body);
            console.log('📨 WebSocket inbox message:', data);
            onMessageReceivedRef.current?.(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        });

        if (userId) {
          stompClient.subscribe(`/topic/notifications/${userId}`, (message) => {
            try {
              const data = JSON.parse(message.body);
              console.log('🔔 WebSocket notification:', data);
              onMessageReceivedRef.current?.(data);
            } catch (error) {
              console.error('Failed to parse WebSocket notification:', error);
            }
          });
        } else {
          console.warn('No userId provided — notification topic not subscribed');
        }
      },
      onStompError: (frame) => {
        console.error('❌ WebSocket STOMP error:', frame);
        setConnectionError('STOMP error: ' + frame.headers?.message);
      },
      onWebSocketError: (error) => {
        console.error('❌ WebSocket error:', error);
        setConnectionError('WebSocket connection failed');
      },
      onDisconnect: () => {
        console.log('🔌 WebSocket disconnected');
        setIsConnected(false);
      },
    });

    stompClient.activate();
    stompClientRef.current = stompClient;

    return stompClient;
  }, [organizationId, userId]);
  const disconnect = useCallback(() => {
    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    const client = connect();
    return () => {
      if (client) {
        client.deactivate();
      }
    };
  }, [connect]);

  const sendMessage = useCallback((destination, payload) => {
    if (stompClientRef.current && isConnected) {
      stompClientRef.current.publish({
        destination: `/app${destination}`,
        body: JSON.stringify(payload),
      });
    } else {
      console.warn('WebSocket not connected, message not sent');
    }
  }, [isConnected]);

  return {
    isConnected,
    connectionError,
    sendMessage,
    disconnect,
    reconnect: connect,
  };
};

export default useWebSocket;